import { makeAutoObservable } from 'mobx';

import {
  CatalogRequests,
  CategoriesResponse,
  LandingPage,
} from '~/api/Catalog';
import { company } from '~/company/Company';

/**
 * Represents a single hero banner item
 */
export interface HeroBannerItem {
  /** Unique identifier of the banner */
  id: number;
  /** Display name of the banner */
  name: string;
  /** Navigation link when banner is clicked */
  deepLink: string;
  /** Desktop image URL */
  previewImage: string;
  /** Mobile image URL (thumbnail) */
  previewImageThumb: string;
}

/**
 * MobX store for managing hero banner data on the main page.
 * Supports SSR preloading and client-side loading.
 */
export class HeroBannerStore {
  /** List of hero banner items to display */
  public items: HeroBannerItem[] = [];
  /** Whether initial data has been loaded */
  public isLoaded = false;
  /** Whether data is currently being fetched */
  public isLoading = false;

  /** SSR preloaded data storage */
  public static preloadedData: CategoriesResponse | null = null;

  constructor() {
    this.init();
    makeAutoObservable(this);
  }

  /**
   * Preloads hero banner data during SSR
   * @param warehouse - Warehouse code
   * @param serverLang - Optional server language
   * @returns Categories response or null if disabled/failed
   */
  public static async preload(
    warehouse: string,
    serverLang?: string,
  ): Promise<CategoriesResponse | null> {
    if (!company.newDesignSystemEnabled) {
      return null;
    }

    try {
      const data = await this.request(warehouse, serverLang);
      this.preloadedData = data;
      return data;
    } catch {
      return null;
    }
  }

  /** Clears preloaded data after SSR hydration */
  public static removePreloadedData() {
    this.preloadedData = null;
  }

  private static request(warehouse: string, serverLang?: string) {
    return CatalogRequests.getCategories(
      {
        warehouseCode: warehouse,
        landingPage: [LandingPage.HERO_BANNER],
        includes: { empty: true },
        page: { size: 25, current: 1 },
      },
      serverLang,
    );
  }

  /** Whether to display the hero banner (has items and feature enabled) */
  public get shouldShow(): boolean {
    return company.newDesignSystemEnabled && this.items.length > 0;
  }

  /** Whether to show the banner section (loading skeleton or banner) */
  public get shouldShowSection(): boolean {
    return (
      company.newDesignSystemEnabled &&
      (this.isLoading || this.items.length > 0)
    );
  }

  /**
   * Loads hero banner data on the client side
   * @param warehouse - Warehouse code
   * @param serverLang - Optional server language
   */
  public async load(warehouse: string, serverLang?: string): Promise<void> {
    if (!company.newDesignSystemEnabled) {
      return;
    }

    this.isLoading = true;

    try {
      const response = await HeroBannerStore.request(warehouse, serverLang);
      this.setItems(response);
    } catch {
      this.setItems(null);
    } finally {
      this.isLoading = false;
    }
  }

  private init() {
    const isSSR = typeof window === 'undefined';
    const preloadedData =
      (!isSSR ? window.__INITIAL_STATE__?.heroBanners : null) ||
      HeroBannerStore.preloadedData;

    if (preloadedData) {
      this.setItems(preloadedData);
    }
  }

  private setItems(response: CategoriesResponse | null) {
    this.items = response ? this.convert(response) : [];
    this.isLoaded = true;
  }

  private convert(response: CategoriesResponse): HeroBannerItem[] {
    return response.data
      .flatMap((item) => item.children ?? [])
      .filter((child) => child.category.images.length === 2)
      .map((child) => ({
        id: child.category.id,
        name: child.category.name,
        deepLink: child.category.previewText ?? '',
        previewImage: child.category.images[0]?.url ?? '',
        previewImageThumb: child.category.images[1]?.url ?? '',
      }));
  }
}

export const heroBannerStore = new HeroBannerStore();
